import { FullViewSwitch } from '@planning/components/atoms/FullViewSwitch'
import { useVesselVisitTableActions } from '@planning/hooks/useVesselVisitTableActions'
import {
  LoadListTableColumnsDto,
  VesselVisitLoadListViewStore,
} from '@planning/pages/VesselVisit/Stores/VesselVisitOrderListViewStore'
import { getLoadListColumns } from '@planning/pages/VesselVisit/tableConfigs/loadListColumns'
import { useTranslate } from '@tolgee/react'
import { TomTable, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { TomTableToolbarAlertBanner } from 'modules/ui/src/theme/components/table/organisms/TomTableToolbarAlertBanner'
import { FC, useEffect, useMemo } from 'react'
import { VisitDashboardTableDetailPanel } from '../../atoms/VisitDashboardTableDetailPanel'
import { OrderListMenuOptions } from '../../molecules/OrderListMenuOptions'
import { ContainerYardOperationViewStore } from '../../OrderGrouping'

type Props = {
  vesselVisitLoadListViewStore: VesselVisitLoadListViewStore
  containerYardOperationViewStore: ContainerYardOperationViewStore
}

export const VesselVisitContainerLoadTable: FC<Props> = observer(
  ({ vesselVisitLoadListViewStore, containerYardOperationViewStore }) => {
    const { t } = useTranslate()

    const getTableActions = useVesselVisitTableActions<LoadListTableColumnsDto>()

    const vesselVisitItem = vesselVisitLoadListViewStore.vesselVisitItem

    const columns = useMemo(() => getLoadListColumns(t), [t])

    const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
      'planning/vessel-visit-container-load',
    )

    const table = useTomTable<LoadListTableColumnsDto>(
      {
        columns,
        data: vesselVisitLoadListViewStore.ordersTableData,
        getRowId: row => row.id.toString(),

        enableRowSelection: row => !!row.original.originalOrder.data.containerNumber?.length,
        renderTopToolbarCustomActions: () => (
          <FullViewSwitch
            onChange={val => vesselVisitLoadListViewStore.setIsFullView(table, val)}
            value={vesselVisitLoadListViewStore.isFullView}
          />
        ),
        renderToolbarAlertBannerContent: ({ table }) => {
          const selectedRows = table.getSelectedRowModel().rows
          const dynamicActions = getTableActions(selectedRows)

          if (!dynamicActions.length) {
            return null
          }

          if (vesselVisitItem.isVesselVisitBlockedForEdit) {
            dynamicActions.forEach(action => {
              action.disabled = true
            })
          }

          return <TomTableToolbarAlertBanner table={table} tableActions={dynamicActions} />
        },
        renderRowActions: ({ row }) => {
          const orderId = row.original.id
          const order = vesselVisitItem?.load?.containerOrders.find(o => o.id === orderId)
          const journey = order?.containerJourney

          return OrderListMenuOptions({ order, journey, containerYardOperationViewStore })
        },
        getRowCanExpand: () => true,

        renderDetailPanel: ({ row }) => <VisitDashboardTableDetailPanel row={row} />,
        muiTableBodyRowProps: ({ row }) => ({
          id: `load-list-row-${row.original.number}`,
        }),
      },
      undefined,
      undefined,
      { columns, savedPreferences, onPreferencesChange },
    )

    useEffect(() => {
      vesselVisitLoadListViewStore.setIsFullView(table, false)
    }, [table, vesselVisitLoadListViewStore])

    return <TomTable table={table} />
  },
)
